<template>
  <div class="main-component-pc-lector-articulo-elaborado-y-actividad">
    <div class="header">
      <div class="header-lefth">
        <span>
          {{ t('production.assignBoxesPallet.lastRead') }}
        </span> <span class="barCode">
          {{ currentBarCodeArticuloElaborado }}
        </span>
      </div>
      <div class="header-right">
        {{ `${listRegistroActividad.length} ${t('common.of')} ${numberContinuousReadings}` }}
      </div>
    </div>

    <UCMultiInputSelector
      ref="multiInputSelectorRef"
      :conf="confUCMultiSelector"
      :allow-change="allowChangeComponentUCMultiInputSelector"
      @change="handleInputChange"
      @validate-input="handleValidateInput"
    />
  </div>
</template>

<script setup>
// @ts-check
import UCMultiInputSelector from '@/components/common/UCMultiInputSelector.vue';
import icCode from '@/static/icons/ic_code.svg';
import icWorkerReport from '@/static/icons/ic_doc_worker_report.svg';
import { notifyReadingError } from '@/js/utils/notifyReadingError';
import { sleep } from '@/js/utils/sleep';
import {
  computed,
  onMounted,
  ref,
  shallowRef,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';

/** @import { PropType, Ref, ShallowRef } from 'vue' */

const props = defineProps({
  // Indica el número de lecturas de actividad por cada articulo elaborado que se tienen que hacer.
  numeroLecturasDeActividadPorCaja: {
    type: Number,
    default: 0,
  },
  // Permite que la camara continue abierta para hacer lecturas mientras no se pulsa el botón back.
  continuousBarCodeReading: {
    type: Boolean,
    default: false,
  },
  orientationScan: {
    type: /** @type {PropType<'portrait' | 'landscape'>} */ (String),
    default: 'portrait',
  },
  // Flash Camara
  torchOn: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits([
  'code-reading-articulo-elaborado',
  'code-reading-registro-actividad',
]);

const { t } = useI18n();
/**
 * Código del último artículo elaborado leído.
 * @type {Ref<string>}
 */
const currentBarCodeArticuloElaborado = ref('');

/**
 * Registros de actividad asociados al artículo elaborado leído.
 * @type {Ref<string[]>}
 */
const listRegistroActividad = ref([]);

// Controla si se permite el cambio utilizando el botón de cambio de input.
const allowChangeComponentUCMultiInputSelector = ref(false);
const numberContinuousReadings = ref(0);
const resetWhenChanging = ref(true);
/** @type {ShallowRef<InstanceType<typeof UCMultiInputSelector> | null>} */
const multiInputSelectorRef = shallowRef(null);

const confUCMultiSelector = computed(() => [
  /** @type {const} */ ({
    id: 'BOX',
    placeholder: t('production.common.boxReading'),
    iconButton: icCode,
    altButton: 'Icono lectura de cajas',
    showBarCodeButton: true,
    confReadBarCode: {
      fabButtonShow: false,
      autoOpenCamera: true,
      autofocusOnInput: false,
      continuousBarCodeReading: props.continuousBarCodeReading,
      orientationScan: props.orientationScan,
      torchOn: props.torchOn,
    },
  }),
  /** @type {const} */ ({
    id: 'ACTIVITY',
    placeholder: t('production.assignBoxesPallet.readActivity'),
    iconButton: icWorkerReport,
    altButton: 'Icono registro de actividad',
    showBarCodeButton: true,
  }),
]);

/**
 * Contiene cual el elemento seleccionado del array de confUCMultiSelector.
 * @type {Ref<typeof confUCMultiSelector['value'][number] | undefined>}
 */
const currentConfMultiSelector = ref(undefined);

/**
 * @public
 * @returns {void}
 */
function reset() {
  // Restablecemos la lista
  listRegistroActividad.value = [];
  currentBarCodeArticuloElaborado.value = '';
  numberContinuousReadings.value = props.numeroLecturasDeActividadPorCaja;

  // Bloqueamos que se pueda regresar a la lectura de caja sin leer actividad.
  allowChangeComponentUCMultiInputSelector.value = false;

  multiInputSelectorRef.value?.selectEntry('BOX', false);
  currentConfMultiSelector.value = confUCMultiSelector.value[0];
}

/**
 * @public
 * @returns {Promise<void>}
 */
async function focus() {
  await sleep(100);
  multiInputSelectorRef.value?.focus();
}

/**
 * @param {typeof confUCMultiSelector['value'][number]} value
 * @returns {void}
 */
function handleInputChange(value) {
  if (resetWhenChanging.value) {
    if (currentConfMultiSelector.value?.id === 'ACTIVITY') {
      reset();
    }
    currentConfMultiSelector.value = value;
  }
}

/**
 * @param {string} val
 * @returns {void}
 */
function handleValidateInput(val) {
  if (!currentConfMultiSelector.value) {
    return;
  }

  if (currentConfMultiSelector.value.id === 'BOX') {
    if (validateArticleElaborated(val)) {
      currentBarCodeArticuloElaborado.value = val;
      multiInputSelectorRef.value?.selectEntry('ACTIVITY', true);
      emit('code-reading-articulo-elaborado', val);
      allowChangeComponentUCMultiInputSelector.value = true; // Permitimos que se pueda regresar a la lectura de caja sin leer actividad.
    }
  } else if (currentConfMultiSelector.value.id === 'ACTIVITY') {
    if (validateActivityRegister(val)) {
      listRegistroActividad.value.push(val);
      emit('code-reading-registro-actividad', val);
      if (listRegistroActividad.value.length >= numberContinuousReadings.value) {
        multiInputSelectorRef.value?.selectEntry('BOX', true);
        // Restablecemos la lista
        listRegistroActividad.value = [];
        currentBarCodeArticuloElaborado.value = '';
        allowChangeComponentUCMultiInputSelector.value = false; // Bloqueamos que se pueda regresar a la lectura de caja sin leer actividad.
      }
    }
  }
}

/**
 * @public
 * @param {string} aeCode
 * @param {number} numberReadings
 * @returns {void}
 */
function setActivityRegister(aeCode, numberReadings) {
  resetWhenChanging.value = false;

  currentBarCodeArticuloElaborado.value = aeCode;
  numberContinuousReadings.value = numberReadings;
  listRegistroActividad.value = [];
  multiInputSelectorRef.value?.selectEntry('ACTIVITY', true);

  resetWhenChanging.value = true;
}

/**
 * @param {string} barCode
 * @returns {boolean}
 */
function validateArticleElaborated(barCode) {
  const boxPrefix = barCode.substring(0, 2);
  if (boxPrefix === '80') {
    notifyReadingError(t('production.info.errorExpectedBoxLabel'));
    return false;
  }
  return true;
}

/**
 * @param {string} barCode
 * @returns {boolean}
 */
function validateActivityRegister(barCode) {
  const boxPrefix = barCode.substring(0, 2);
  if (boxPrefix !== '80') {
    notifyReadingError(t('production.info.errorExpectedRegistrationLabel'));
    return false;
  }
  return true;
}

watch(() => props.numeroLecturasDeActividadPorCaja, (val) => {
  numberContinuousReadings.value = val;
});

onMounted(() => {
  numberContinuousReadings.value = props.numeroLecturasDeActividadPorCaja;
  currentConfMultiSelector.value = confUCMultiSelector.value[0];
});

defineExpose({
  focus,
  reset,
  setActivityRegister,
});
</script>

<style lang="scss">
.main-component-pc-lector-articulo-elaborado-y-actividad {
  .header {
    display: flex;
    justify-content: space-between; // Esto coloca los elementos al inicio y al final
    color: #24253d;
    text-transform: none;
    font-size: 17px;
    padding-left: 25px;
    padding-top: 5px;
    padding-bottom: 5px;
    padding-right: 12px;
    border-bottom: 4px;
    border-top: 0;
    border-right: 0;
    border-left: 0;
    border-color: var(--his-disabled);
    border-style: solid;
    background: aliceblue;

    .header-lefth {
      padding-top: 3px;
      color: var(--his-steel-blue);
      font-size: 14px;
      font-weight: 400;

      .barCode {
        font-weight: 600;
      }
    }
  }
}
</style>
